import { useState, useEffect, useCallback } from 'react';

const useColumnPinning = (tableContainerRef, dependencies = []) => {
  const [columnPinning, setColumnPinning] = useState({
    left: [],
    right: []
  });

  const [columnWidths, setColumnWidths] = useState(new Map());

  const getCommonPinningStyles = useCallback((column, table) => {
    const isPinned = column.getIsPinned();

    let leftPosition = undefined;
    if (isPinned === 'left') {
      const allHeaders = table.getHeaderGroups()[0]?.headers || [];
      const leftPinnedHeaders = allHeaders.filter(h => h.column.getIsPinned() === 'left');
      const currentIndex = leftPinnedHeaders.findIndex(h => h.id === column.id);

      if (currentIndex === 0) {
        leftPosition = '0px';
      } else {
        let cumulativeWidth = 0;
        for (let i = 0; i < currentIndex; i++) {
          const prevColumnId = leftPinnedHeaders[i].id;
          const actualWidth = columnWidths.get(prevColumnId) || 200;
          cumulativeWidth += actualWidth;
        }
        leftPosition = `${cumulativeWidth}px`;
      }
    }

    return {
      position: isPinned ? 'sticky' : 'relative',
      left: leftPosition,
      right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
      zIndex: isPinned ? 1 : 0,
      boxShadow: isPinned === 'left' ? '2px 0 4px -2px rgba(0, 0, 0, 0.1)' : undefined,
    };
  }, [columnWidths]);

  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    if (!tableContainer) return;

    const measureColumnWidths = () => {
      const headerCells = tableContainer.querySelectorAll('thead th[data-column-id]');
      const newWidths = new Map();

      headerCells.forEach((cell) => {
        const columnId = cell.getAttribute('data-column-id');
        if (columnId) {
          const rect = cell.getBoundingClientRect();
          newWidths.set(columnId, rect.width);
        }
      });

      setColumnWidths(newWidths);
    };

    const timeoutId = setTimeout(measureColumnWidths, 100);

    const resizeObserver = new ResizeObserver(() => {
      measureColumnWidths();
    });

    const headerCells = tableContainer.querySelectorAll('thead th[data-column-id]');
    headerCells.forEach(cell => resizeObserver.observe(cell));

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [tableContainerRef, columnPinning, ...dependencies]);

  return {
    columnPinning,
    setColumnPinning,
    getCommonPinningStyles,
  };
};

export default useColumnPinning;
